/* eslint-disable indent */
import {
  DateTimeValidationError,
  LocalizationProvider,
  MobileDatePicker,
  MobileDateTimePicker,
  PickerChangeHandlerContext,
  DatePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { frFR } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutsideDateTimePicker } from './hooks/useClickOutside.hooks';
import './styles.css';

interface DateTimePickerProps<Fieldname extends string> {
  field?: ControllerRenderProps<FieldValues, Fieldname>;
  useTime?: boolean;
  allowClear?: boolean;
  disabled?: boolean;
  value?: dayjs.Dayjs;
  onChange?: (
    value: dayjs.Dayjs,
    context: PickerChangeHandlerContext<DateTimeValidationError>
  ) => void;
  format?: string;
  disablePast?: boolean;
  variant?: 'desktop' | 'mobile';
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  onClose?: () => void;
  open?: boolean;
  closeOnClickOutside?: boolean;
}

export const DateTimePicker = <Fieldname extends string>({
  field,
  useTime = true,
  allowClear = true,
  disabled = false,
  value,
  onChange,
  format,
  disablePast = true,
  variant = 'desktop',
  minDate,
  maxDate,
  onClose,
  open: controlledOpen,
  closeOnClickOutside = true,
}: DateTimePickerProps<Fieldname>) => {
  const { t } = useTranslation('common');
  const isDesktopVariant = variant === 'desktop';
  const [internalOpen, setInternalOpen] = useState(false);

  /**
   * === Handle click outside ===
   */
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const handleClose = () => {
    if (controlledOpen === undefined) {
      setInternalOpen(false);
    }
    onClose?.();
  };

  const handleOpen = () => {
    if (controlledOpen === undefined) {
      setInternalOpen(true);
    }
  };

  const clickOutsideRef = useClickOutsideDateTimePicker({
    onClickOutside: closeOnClickOutside ? handleClose : () => {},
    isOpen,
  });
  /**
   * === END OF Handle click outside ===
   */

  const Picker = isDesktopVariant
    ? DatePicker
    : useTime
    ? MobileDateTimePicker
    : MobileDatePicker;
  format = useTime ? 'DD-MM-YYYY HH:mm' : 'DD-MM-YYYY';

  return (
    <div ref={clickOutsideRef}>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="fr"
        localeText={{
          ...frFR.components.MuiLocalizationProvider.defaultProps.localeText,
          cancelButtonLabel: t('cancel'),
          clearButtonLabel: t('clear'),
        }}
      >
        <Picker
          {...field}
          value={value}
          onChange={onChange}
          format={format}
          disabled={disabled}
          disablePast={disablePast}
          minDate={minDate}
          maxDate={maxDate}
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          slotProps={
            isDesktopVariant
              ? {
                  actionBar: {
                    actions: allowClear ? ['clear'] : [],
                  },
                  popper: {
                    placement: 'bottom-start',
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: true,
                        options: {
                          altBoundary: true,
                          rootBoundary: 'document',
                          padding: 8,
                        },
                      },
                      {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                          altAxis: true,
                          altBoundary: true,
                          tether: true,
                          rootBoundary: 'document',
                          padding: 8,
                        },
                      },
                    ],
                  },
                }
              : {
                  dialog: {
                    onClose: handleClose,
                  },
                }
          }
        />
      </LocalizationProvider>
    </div>
  );
};
